import React from 'react';
import { formatMoney } from '../../lib/formatMoney';

/**
 * Client-facing "What this is for" block on the pay page: a two-column table
 * (description + right-aligned amount) with a total row. Lines without an
 * amount leave the Amount cell blank. Renders nothing when there are no items.
 */
export const LineItemsSummary = ({
  items = [],
  totalLabel = 'Due now',
  totalAmount = 0,
  balanceRow = null,
  testPrefix = 'cv-line-items',
}) => {
  const rows = (items || []).filter((it) => (it?.description || '').trim());
  if (!rows.length) return null;

  // The client already knows this is the retainer and the date sits up top, so
  // "Retainer — Corporate Event, Dec 20 (TEST)" reads as just the event name.
  const tidy = (text) => String(text || '')
    .replace(/^\s*(retainer|deposit|balance|final balance)\s*[—–-]\s*/i, '')
    .replace(/,\s*(jan|feb|mar|apr|may|jun|jul|aug|sep|sept|oct|nov|dec)[a-z]*\.?\s*\d{1,2}(st|nd|rd|th)?(,?\s*\d{4})?/i, '')
    .trim();

  const LINE = 'rgba(255,255,255,0.08)';
  const MUT = '#8f9099';

  return (
    <div
      style={{
        marginTop: 18, borderRadius: 14, padding: '16px 18px',
        background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)',
      }}
      data-testid={`${testPrefix}-block`}
    >
      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingBottom: 10, borderBottom: `1px solid ${LINE}`,
          fontSize: 10.5, letterSpacing: '.14em', textTransform: 'uppercase',
          color: MUT, fontWeight: 700,
        }}
      >
        <span>What this is for</span>
        <span>Amount</span>
      </div>

      {rows.map((it, i) => (
        <div
          key={i}
          style={{
            display: 'flex', justifyContent: 'space-between', gap: 14,
            padding: '11px 0', borderBottom: `1px solid ${LINE}`,
          }}
          data-testid={`${testPrefix}-row`}
        >
          <span style={{ fontSize: 13.5, color: '#E7E3D8', fontWeight: 500, lineHeight: 1.4 }}>
            {tidy(it.description)}
          </span>
          <span style={{ fontSize: 13.5, color: '#C6C8CF', fontWeight: 500, whiteSpace: 'nowrap', textAlign: 'right' }}>
            {it.amount === null || it.amount === undefined || it.amount === ''
              ? ''
              : formatMoney(Number(it.amount))}
          </span>
        </div>
      ))}

      <div
        style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingTop: 13,
        }}
      >
        <span style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: MUT, fontWeight: 700 }}>
          {totalLabel}
        </span>
        <span style={{ fontSize: 16, fontWeight: 600, color: '#D7DBE3' }} data-testid={`${testPrefix}-total`}>
          {formatMoney(Number(totalAmount) || 0)}
        </span>
      </div>

      {balanceRow && (
        <div
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
            paddingTop: 12, marginTop: 12, borderTop: `1px solid ${LINE}`,
          }}
          data-testid={`${testPrefix}-balance-later`}
        >
          <span style={{ fontSize: 11, letterSpacing: '.14em', textTransform: 'uppercase', color: MUT, fontWeight: 700 }}>
            Balance due later
          </span>
          <span style={{ textAlign: 'right' }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: '#C9C4B6' }} data-testid={`${testPrefix}-balance-amount`}>
              {formatMoney(Number(balanceRow.amount) || 0)}
            </span>
            {balanceRow.dueLabel && (
              <span style={{ display: 'block', fontSize: 11, color: MUT, marginTop: 2 }}>
                due {balanceRow.dueLabel}
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
};

export default LineItemsSummary;
